import {gsap} from "gsap";
import {FireInTheHole2} from "../../FireInTheHole2";
import {BitmapText, Sprite} from "pixi.js";
import {FITH2Helper} from "../../utils/FITH2Helper";
import Spine = PIXI.spine.Spine;
import {SimpleReelModelState} from "../../reels/freespin/FreespinReelModel";
import {FITH2FontConfig} from "../../configs/FITH2FontConfig";
import {SpineTween} from "@nolimit/slot-game/bin/animation/SpineTween";
import {NolimitConfig} from "@nolimit/slot-launcher/bin/settings/NolimitConfig";
import {FITH2GameConfig} from "../../configs/FITH2GameConfig";
import {FITH2SoundManager} from "../../sound/FITH2SoundManager";
import {TimelineSprite} from "@nolimit/promo-panel/bin/utils/TimelineSprite";
import {MathHelper} from "@nolimit/slot-game/bin/utils/MathHelper";

export enum FSSymbolTypes {
    MISS,
    DIRT,
    COIN,
    DYNAMITE,
    MULTIPLIER,
    COLLECTOR,
    NECROMANCER
}

export interface IFsSymbolData {
    symName: string,
    value?: number,
    type?: FSSymbolTypes,
    id?: number
    reelId?: number
    state?: SimpleReelModelState
}

export class FreespinSymbol extends PIXI.Container {
    private readonly normalName: string[] = ["M", "D", "C", "DY", "R", "DC", "N"];
    private readonly upgradeName: string[] = ["M", "D", "PC", "PY", "PR", "PD", "PN"];

    private _id: number = -1;
    private _reelPosition: number = -1;
    private _name: string;

    protected _spine: Spine | null;
    private _featureSpine: Spine | null;
    private _spinImage: Sprite;
    private _missSprite: Sprite;
    private _dirt: Sprite;

    private _featureValue: number;
    protected _value: number;
    protected _reelId: number = -1;
    protected _valueText: BitmapText;

    //types
    protected _type: FSSymbolTypes;
    protected _isDirt: boolean = false;
    protected _isBrown: boolean = true;
    protected _isUpgrade: boolean = false;
    protected _isRevealed: boolean = false;
    private _isSpinning: boolean = false;
    private _isRemoved: boolean = false;
    private _trackEntry:any;

    public get isPC(): boolean {
        return this._name === "PC";
    }

    private onRestore(symbolData: IFsSymbolData): void {
        if (symbolData.symName[0] === "P") {
            this._featureValue = symbolData.symName === "PD" ? 0 : parseInt(symbolData.symName.substring(2));
            this.isUpgraded = symbolData.type!;
            this.showFeature()
        } else {
            if (symbolData.value! > 0) {
                this._isBrown = symbolData.value! < 2;
                this._spine!.skeleton.setSkinByName(this._isBrown ? "Brown" : "Gold");
                this._spine!.visible = true;
            }
        }
        this._value = symbolData.value!;
        this.updateValueText();
    }

    protected createCoin(): void {
        if (this._spine) {
            this.removeChild(this._spine);
            this._spine.destroy();
        }
        this._spine = new PIXI.spine.Spine(FireInTheHole2.assetLoader.getSpineAsset("coin"));
        this._spine.name = this.name + "Coin";
        this._spine.position.set(0, 0);

        this._trackEntry = null;
        this._trackEntry = this._spine.state.setAnimation(1, "value_glow", true);
        this._trackEntry.alpha = 0; // 0-1 value (percent)
    }

    protected createPersistentComponents(): void {
        this._missSprite = FITH2Helper.createSprite("coin/M", 0, 0);
        this._missSprite.anchor.set(0.5);
        this._missSprite.visible = false;
        this.addChild(this._missSprite);
    }

    protected createSpinComponents(): void {
        this._spinImage = new PIXI.Sprite();
        this._spinImage.name = "spinImage";
        this._spinImage.anchor.set(0.5, 0.5);
    }

    protected createDirt(): void {
        if (this._dirt) {
            return;
        }
        this._dirt = FITH2Helper.createSprite("D2/D2", 0, 0);
        this._dirt.name = "DIRT";
        this._dirt.anchor.set(0.5);
        this.addChild(this._dirt);
    }

    public reset(): void {
        this._value = 0;
        if (this._type === FSSymbolTypes.COLLECTOR) {
            console.log("value: " + this._value);
        }
        this.updateValueText(false);
        this._type = FSSymbolTypes.MISS;
        this._isRevealed = false;
        this._isUpgrade = false;
        this.symbolSprite = "M";
        if (this._spine) {
            this._spine.visible = false;
        }

        if (this._featureSpine) {
            this._spine!.visible = false;
        }
    }

    public init(symData: IFsSymbolData, isRestore: boolean): void {
        this.addChild(this._spine!, this._valueText);
        if (isRestore && this._type > 0) {
            this._isRevealed = true;
            this.onRestore(symData);
        }
    }

    constructor(symbolData: IFsSymbolData, state: SimpleReelModelState, isRestore: boolean = false) {
        super();
        this.createSpinComponents();
        if (!symbolData.type) {
            this.name = `fsSpin${symbolData.reelId}`;
            this._isSpinning = true;
            let symName: string = `coinSpin/${symbolData.symName}_spin`;
            if (state != SimpleReelModelState.SPIN) {
                symName = `${symbolData.symName === "M" ? "coin/M" : (symbolData.symName[0] === "B" ? "H/H_Brown" : "H/H_Gold")}`;
            }
            this.symbolSprite = symName;
            return;
        }
        this.name = `fs${symbolData.reelId}${this._id}`;
        this._isSpinning = false;
        this._isRemoved = false;
        this._id = symbolData.id!;
        this._name = symbolData.symName;
        this._reelId = symbolData.reelId!;
        this._type = symbolData.type!;
        this._isUpgrade = this._name[0] === "P";
        this._isRevealed = false;
        this._value = 0;
        this.createCoin();
        this.createPersistentComponents();

        this._valueText = new BitmapText("", FITH2FontConfig.FS_COIN_MULTI);
        this._valueText.position.set(0, 0);
        this._valueText.anchor = new PIXI.Point(0.5, 0.5);
        this.init(symbolData, isRestore);
    }

    public updateValueText(withAnimation: boolean = true): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._value = Math.min(this._value, FITH2GameConfig.WIN_CAP);
            this._valueText.text = `${this._value}x`;
            this._valueText.scale.set(Math.min(100 / this._valueText.width, 1));
            this._valueText.visible = (this._value > 0);
            this._trackEntry!.alpha = MathHelper.roundToDecimals(this._value/100,2);
        }, 0);
        if (withAnimation) {
            tl.add(gsap.to(this._valueText.scale, 3 * 0.033, {x: 1.4, y: 1.4}), 0);
        }
        tl.add([
            gsap.to(this._valueText.scale, withAnimation ? 5 * 0.033 : 0, {x: 1, y: 1}),
            () => {
                if (this._value > 0 && this._type === FSSymbolTypes.COIN) {
                    if (this._value < 2) {
                        this._spine?.skeleton.setSkinByName("Brown");
                    } else if (this._isBrown) {
                        this._isBrown = false;
                        this._spine?.skeleton.setSkinByName("Gold");
                    }
                }
            }
        ]);
        return tl;
    }

    public collect(): gsap.core.Timeline {
        return gsap.timeline().add([
            gsap.to(this.scale, 0.2, {x: 1.2, y: 1.2, ease: "power3.out"}),
            gsap.to(this.scale, 0.3, {x: 1, y: 1, ease: "power3.out", delay: 0.2})
        ]);
    }

    public remove(): FreespinSymbol {
        this._isRemoved = true;
        if (this._dirt) {
            this.removeChild(this._dirt);
        }

        if (this._spinImage) {
            this.removeChild(this._spinImage);
        }

        if (this._spine) {
            this._spine.visible = false;
            this.removeChild(this._spine);
            //this._spine.destroy()
        }

        if (this._featureSpine) {
            this.removeChild(this._featureSpine);
            //this._featureSpine.destroy();
        }

        if (this._valueText) {
            this._valueText.destroy();
            this.removeChild(this._valueText);
        }

        if (this.parent) {
            this.parent.removeChild(this);
        }

        this.destroy({children: true});
        return this;
    }

    private showCoin(): void {
        if (this._isUpgrade) {
            return;
        }
        if (!this._spine) {
            this.createCoin();
            this.addChild(this._spine!, this._valueText);
        }
        this._name = "C";
        this._spine!.visible = true;
        this._featureValue = 0;
        if (this._featureSpine) {
            this._featureSpine!.visible = false;
        }
        if (this._missSprite) {
            this._missSprite.visible = false;
        }
    }

    public removeFeature(): gsap.core.Timeline {
        if (this._isUpgrade) {
            return gsap.timeline();
        }
        this._type = FSSymbolTypes.COIN;
        const tl = gsap.timeline();
        tl.add(new SpineTween(this._featureSpine!, 0, "to_coin"));
        tl.add(() => {
            this.showCoin();
        });

        return tl;
    }

    public shakePCChainAnimation(): gsap.core.Timeline {
        return gsap.timeline().add(()=>{this._featureSpine!.state.setAnimation( 0, "shake", true);});
    }

    public removeChain(): gsap.core.Timeline {
        return gsap.timeline().add(new SpineTween(this._featureSpine!, 0, "to_coin"));
    }

    public spin(delay: number = 1): gsap.core.Timeline {
        if (this._isDirt || this._isRevealed) {
            return new gsap.core.Timeline();
        }

        this._isSpinning = true;
        return new gsap.core.Timeline();
    }

    private miss(): gsap.core.Timeline {
        this.symbolSprite = "M";
        return gsap.timeline().add(() => {
            FITH2SoundManager.playFSMissSound()
        }, 0.01);
    }

    private hit(): gsap.core.Timeline {
        const value: number = FireInTheHole2.model.fsReelsData[this._reelId][0].value!,
            tl = gsap.timeline();

        if (!this._isRevealed) {
            this.addChild(this._spine!, this._valueText);
            tl.add([
                () => {
                    FireInTheHole2.keypadPlugin.setZeroBetSpinCounter(3);
                    this._spine!.skeleton.setSkinByName((value > 1 || this._value > 1) ? "Gold" : "Brown");
                },
                new SpineTween(this._spine!, 0, "land"),
                FireInTheHole2.fsLeftView.update(3)
            ]);
        }
        this._isRevealed = true;
        return tl;
    }

    public dropValueFromTopTrain(): gsap.core.Timeline {
        const topSymbol: IFsSymbolData = FireInTheHole2.model.fsReelsData[this._reelId][0],
            topSymbolType: FSSymbolTypes = topSymbol.type!;
        let value: number = FireInTheHole2.model.fsReelsData[this._reelId][0].value!,
            isMultiplier: boolean = false;
        if (topSymbolType === FSSymbolTypes.MULTIPLIER || topSymbolType === FSSymbolTypes.NECROMANCER) {
            value = (this._value === 0 ? 1 : 0);
        }
        this._value = (this._value + value);
        if (this._name === "PC") {
            this._name = `${this.normalName[FSSymbolTypes.COIN]}`;
            this._isUpgrade = false;
        }
        if (NolimitConfig.isDevMode && this._value > FireInTheHole2.model.data.coinValues[this._reelId][this._id]) {
            debugger
        }
        const tl = gsap.timeline();
        let object: BitmapText | PIXI.Sprite, toX: number = 0, toY: number = 0;
        if (topSymbolType === FSSymbolTypes.DYNAMITE) {
            object = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`bomb${value}`)[0]);
        } else if (topSymbolType === FSSymbolTypes.COLLECTOR) {
            object = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`DC/DC`)[0]);
        } else if (topSymbolType === FSSymbolTypes.NECROMANCER) {
            object = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`N/N`)[0]);
        } else if (topSymbolType === FSSymbolTypes.MULTIPLIER) {
            object = new BitmapText(`x${FireInTheHole2.model.fsReelsData[this._reelId][0].value!}`, FITH2FontConfig.REEL_MULTIPLIER_VALUE);
            object.scale.set(2.5);
            isMultiplier = true;
        } else if (value > 0) {
            object = new BitmapText(`${value}x`, FITH2FontConfig.FS_COIN_MULTI_FLY);
            object.scale.set(1.3);
        }

        object!.anchor = new PIXI.Point(0.5, 0.5);
        object!.position.set(0, -this._id * 100);
        tl.add([
            () => {
                if (object) {
                    this.addChild(object!);
                }
            },
            gsap.to(object!.scale, (this._id + 1) * 0.1, {y: isMultiplier ? 2 : 1, x: isMultiplier ? 2 : 1}),
            gsap.to(object!, (this._id) * 0.1, {x: toX, y: toY, delay: 0.1})
        ], 0);
        tl.add([
            this.addCoinValue(),
            () => {
                object.destroy();
            }
        ]);
        return tl;
    }

    public playLandAnimation(): gsap.core.Timeline {
        return new SpineTween(this._spine!, 0, "land");
    }
    public stop(symbolData: IFsSymbolData, fromInit: boolean = false): gsap.core.Timeline {
        this._isDirt = symbolData.symName === "D";
        if (fromInit) {
            this.onRestore(symbolData)
            return gsap.timeline();
        }
        const tl = gsap.timeline();
        //TODO
        if (this._isDirt || (this._isRevealed && this._name != "PC")) {
            return tl;
        }
        this._isSpinning = false;
        if (symbolData.symName === "M") {
            return this.miss();
        }
        return this.hit();
    }

    private getLandingCoinType(value: number): string {
        //1,5,10,25,50,100
        if (value < 6) {
            return "Small";
        }
        if (value < 20) {
            return "Medium";
        }
        return "Large";
    }

    public addCoinValue(): gsap.core.Timeline {
        const topSymbol: IFsSymbolData = FireInTheHole2.model.fsReelsData[this._reelId][0],
            topSymbolType: FSSymbolTypes = topSymbol.type!,
            isNewFeature: boolean = topSymbolType != FSSymbolTypes.COIN,
            isUpgradeCell: boolean = FireInTheHole2.model.data.upgradeColumn[this._id - 1],
            tl = gsap.timeline();

        if (isNewFeature) {
            this._name = topSymbol.symName;
            this._featureValue = topSymbol.value!;
        }
        this._type = topSymbolType;
        if (isUpgradeCell || isNewFeature) {
            if (isUpgradeCell) {
                this.isUpgraded = topSymbolType;
            }
            tl.add(this.showFeature(), 0);
        } else {
            if (!(this._value === 1 && this._type === FSSymbolTypes.COIN)) {
                tl.add(new SpineTween(this._spine!, 0, "coin_hit"), 0);
            }
        }
        tl.add([
            () => {
                if(topSymbol.symName[0] === "R"){
                    FITH2SoundManager.playSound(`fsMultiLandsOnCoin${this.getLandingCoinType(topSymbol.value!)}`);
                }
            },
            this.updateValueText(true)
        ], 0);
        return tl;
    }

    public playFeatureTrigger(): gsap.core.Timeline {
        if (!this._featureSpine) {
            debugger
        }
        const tl = gsap.timeline();
        tl.add(new SpineTween(this._featureSpine!, 0, "trigger"));
        return tl;
    }

    public getDynamite(stepId: number, explodeOnTopLayer:boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline(),
            explodeEffect: TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("W_explde_eff/W_explode_eff_")),
            explodeDirtEffect: TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("D_break/D_break_")),
            dirtExplode: boolean = this._isDirt;
        if (dirtExplode) {
            this._name = "M";
            this._isDirt = false;
            this._isSpinning = false;
            this._spinImage.alpha = 0;
            tl.add([
                () => {
                    explodeDirtEffect.anchor.set(0.5, 0.5);
                    explodeDirtEffect.position.set(0, 0);
                    this.addChild(explodeDirtEffect);
                },
                explodeDirtEffect.getAnimationAutoShowHide()
            ], 0);
            tl.add([
                () => {
                    if(!explodeOnTopLayer){
                        FITH2SoundManager.playDynamiteStoneSound();
                    }
                    this._dirt.visible = false;
                    this.addChild(this._spinImage);
                    this.symbolSprite = "M";
                },
                gsap.to(this.scale, 0.3, {x: 1.1, y: 1.1, ease: "power3.out"}),
                gsap.to(this.scale, 0.2, {x: 1, y: 1, ease: "power3.out", delay: 0.3}),
                gsap.to(this._spinImage, 0.2, {alpha: 1, delay: 0.3})
            ], 0.2);
        } else {
            tl.add(() => {
                FITH2SoundManager.playSound("fsDynamiteLandsOnCoin");
                this._value = (stepId < 0 ? 0 : FireInTheHole2.model.fsSteps[stepId].endMultipliers[this._reelId][this._id - 1]);
            }, 0);
            tl.add(this.updateValueText(), 0.2);
        }
        if(!explodeOnTopLayer){
            tl.add([
                () => {
                    explodeEffect.blendMode = PIXI.BLEND_MODES.ADD;
                    explodeEffect.anchor.set(0.5, 0.5);
                    explodeEffect.scale.set(0.35);
                    explodeEffect.position.set(0, 0);
                    this.addChild(explodeEffect);
                    this.addChild(this._valueText);
                },
                explodeEffect.getAnimationAutoShowHide()
            ], 0);
        }
        tl.add(() => {
            if(!explodeOnTopLayer){
                this.removeChild(explodeEffect);
            }
            if (dirtExplode) {
                this.removeChild(explodeDirtEffect);
            }
        });

        return tl;
    }

    public getBeer(): gsap.core.Timeline {
        this._isUpgrade = true;
            const feature: string = this.normalName[FSSymbolTypes.COIN],
                featureSpine:Spine = new PIXI.spine.Spine(FireInTheHole2.assetLoader.getSpineAsset(feature));
            featureSpine.name = this.name + "F_" + feature;
            return gsap.timeline().add(()=>{
                this._type = FSSymbolTypes.COIN;
                this._isUpgrade = true;
                this._name = `${this.upgradeName[this._type]}`;
                this._spine!.visible = true;
                this.addChild(featureSpine, this._valueText);
                this.removeChild(this._featureSpine!);
                this._featureSpine = null;
                this._featureSpine = featureSpine;
                this._featureSpine!.skeleton.setSkinByName( "upgraded");
            });
    }

    public crossFadeFromSpin(duration: number = 1): gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this._spine, {alpha: 1, duration: duration}))
            .add(gsap.to(this._spinImage, {alpha: 1, duration: duration}), "<");
    }

    public crossFadeIntoSpin(duration: number = 1): gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this._spine, {alpha: 0, duration: duration}))
            .add(gsap.to(this._spinImage, {alpha: 1, duration: duration}), "<");
    }

    public reelMultiplierEnd(): gsap.core.Timeline {
        if (this._isUpgrade) {
            const feature: string = this.normalName[FSSymbolTypes.COIN],
                featureSpine:Spine = new PIXI.spine.Spine(FireInTheHole2.assetLoader.getSpineAsset(feature));
            featureSpine.name = this.name + "F_" + feature;
            return gsap.timeline().add(()=>{
                this._type = FSSymbolTypes.COIN;
                this._isUpgrade = true;
                this._name = `${this.upgradeName[this._type]}`;
                this._spine!.visible = true;
                this.addChild(featureSpine, this._valueText);
                this.removeChild(this._featureSpine!);
                this._featureSpine = null;
                this._featureSpine = featureSpine;
                this._featureSpine!.skeleton.setSkinByName( "upgraded");
            });
        }
        this._isUpgrade = false;
        return this.removeFeature();
    }

    public getReelMultiplier(stepId: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._value = (stepId < 0 ? 0 : FireInTheHole2.model.fsSteps[stepId].endMultipliers[this._reelId][this._id - 1]);
        });
        tl.add([
            ()=>{
                FITH2SoundManager.playSound(`fsMultiLandsOnCoin${this.getLandingCoinType(FireInTheHole2.model.fsReelsData[this._reelId][0].value!)}`);
            },
            this.updateValueText()
        ]);
        return tl;
    }

    public dwarfCollectStart(): gsap.core.Timeline {
        return gsap.timeline().add([
            () => {
                FITH2SoundManager.playSound(this._isUpgrade?"fsDwarfCollectPersistent":"fsDwarfCollect");
            },
            new SpineTween(this._featureSpine!, 0, "trigger"),
        ]);
    }

    public dwarfCollectAnimation(multiplier: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._value = multiplier;
        });
        tl.add([
            () => {
                this.updateValueText();
                FITH2SoundManager.playSound("fsDwarfMultiLands");
            },
            gsap.to(this._valueText, 0.3, {y: 0, ease: "power3.out", delay: 0.2})
        ], 0.2);
        tl.add(new SpineTween(this._featureSpine!, 0, "trigger_end"), 0);
        tl.add(this.removeFeature());
        return tl;
    }

    public showFeature(): gsap.core.Timeline {
        if (this._type === FSSymbolTypes.COIN) {
            this._spine!.visible = true;
        }
        const feature: string = this.normalName[this._type],
            featureSpine:Spine = new PIXI.spine.Spine(FireInTheHole2.assetLoader.getSpineAsset(feature));
        featureSpine.name = this.name + "F_" + feature;

        return gsap.timeline().add([
            () => {
                this.addChild(featureSpine, this._valueText);
                if (this._featureSpine) {
                    this.removeChild(this._featureSpine!);
                    this._featureSpine = null;
                }
                this._featureSpine = featureSpine;

                if (this._type === FSSymbolTypes.DYNAMITE || this._type === FSSymbolTypes.MULTIPLIER) {
                    this._featureSpine.state.setAnimation(1, `value_${this._featureValue}`, false);
                }

            this._spine!.visible = this._type === FSSymbolTypes.COIN;
            this._featureSpine!.visible = true;
            this._featureSpine!.skeleton.setSkinByName(this._isUpgrade ? "upgraded" : "normal");
            this.addChild(this._featureSpine!, this._valueText);
            if (this._type === FSSymbolTypes.COLLECTOR) {
                FITH2SoundManager.playSound(this._isUpgrade?"fsUpgradeDwarf":"fsDwarfLands");
            } else if (this.type === FSSymbolTypes.NECROMANCER) {
                FITH2SoundManager.playSound(this._isUpgrade?"fsUpgradeBeer":"fsCoinLandingBeer");
            } else if (this.type === FSSymbolTypes.COIN) {
                FITH2SoundManager.playSound("fsCoinReactivated");
            } else if (this.type === FSSymbolTypes.DYNAMITE) {
                if (this._isUpgrade) {
                    FITH2SoundManager.playSound("fsUpgradeDynamite");
                }
            }

        },
            new SpineTween(featureSpine!, 0, `land${this._isUpgrade ? "_upgraded" : ""}`)
        ]);
    }

    public set isUpgraded(type: FSSymbolTypes) {
        this._type = type;
        if (type === FSSymbolTypes.MISS) {
            this._name = `${this.normalName[type]}`;
            this._isUpgrade = false;
            this.removeFeature();
            return;
        }
        this._isUpgrade = true;
        this._name = `${this.upgradeName[type]}`;
    }

    public set symbolSprite(textureName: string) {
        if (this._isSpinning) {
            this._spinImage.visible = true;
            this._spinImage.texture = FireInTheHole2.assetLoader.getTextures(textureName)[0];
            this.addChild(this._spinImage);
            return;
        }
        if (textureName === "D") {
            this.createDirt();
        } else if (textureName === "M") {
            this._spinImage.name = "spinImageM";
            this._spinImage.texture = FireInTheHole2.assetLoader.getTextures("coin/M")[0];
        }
    }

    public get symName(): string {
        return this._name;
    }

    public get id(): number {
        return this._id;
    }

    public get reelId(): number {
        return this._reelId;
    }

    public get state(): FSSymbolTypes {
        return this._type;
    }

    public get isRevealed(): boolean {
        return this._isRevealed;
    }

    public get isDirt(): boolean {
        return this._isDirt;
    }

    public set isDirt(value: boolean) {
        this.symbolSprite = "D";
        this._isDirt = true;
        this._dirt.visible = true;
    }

    public set reelPosition(value: number) {
        this._reelPosition = value;
    }

    public get reelPosition(): number {
        return this._reelPosition;
    }

    public get featureValue(): number {
        return this._featureValue;
    }

    public get value(): number {
        return this._value;
    }

    public get type(): FSSymbolTypes {
        return this._type;
    }

    public get isRemoved(): boolean {
        return this._isRemoved;
    }

    public get isSpinning(): boolean {
        return this._isSpinning;
    }

    public set isSpinning(value: boolean) {
        this._isSpinning = true;
    }
}